import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  Button,
  Select,
  Col,
  Row,
  Input,
  InputNumber,
  Table,
  DatePicker,
  Popconfirm,
  Tabs,
  Form,
  message,
  Spin,
} from "antd";
import moment from "moment";
import { platformApi } from "../../../../../api";

const { Option } = Select;
const { TabPane } = Tabs;

const StockInwardModal = ({
  visible,
  onClose,
  onSuccess,
  editData = null,
  branches = [],
  currentBranchId = null,
  masterBinLocations = [],
}) => {
  const [saving, setSaving] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState(currentBranchId || "");
  const [inwardDate, setInwardDate] = useState(moment());

  // Bins table: [{ binLocation: string, parts: [{ partId, partNumber, partName, quantity }] }]
  const [binsTable, setBinsTable] = useState([
    { binLocation: "", parts: [] },
  ]);
  const [activeBinKey, setActiveBinKey] = useState("0");

  // Table search text to instantly filter parts in active bin
  const [tableSearchText, setTableSearchText] = useState("");

  // Part search state
  const [partOptions, setPartOptions] = useState([]);
  const [partSearchText, setPartSearchText] = useState("");
  const [searchingParts, setSearchingParts] = useState(false);
  const partSearchDebounce = useRef(null);

  // Focus ref for bin input
  const binInputRef = useRef(null);

  // Hardware Barcode Scanner HID Gun Rapid Burst Buffer (< 50ms interval)
  const scannerBufferRef = useRef("");
  const lastKeyTimeRef = useRef(0);

  // Initialize or populate data on open
  useEffect(() => {
    if (visible) {
      if (editData && editData.id) {
        // Edit Mode
        setSelectedBranchId(editData.branch || (editData.Branch && editData.Branch.id) || currentBranchId || "");
        setInwardDate(
          editData.inwardDate
            ? moment(editData.inwardDate, ["DD/MM/YYYY", "YYYY-MM-DD", moment.ISO_8601])
            : moment()
        );

        let parsedBins = [];
        if (editData.inwardData) {
          parsedBins =
            typeof editData.inwardData === "string"
              ? JSON.parse(editData.inwardData)
              : editData.inwardData;
        } else if (Array.isArray(editData.bins)) {
          parsedBins = editData.bins;
        }

        if (Array.isArray(parsedBins) && parsedBins.length > 0) {
          setBinsTable(parsedBins);
        } else {
          setBinsTable([{ binLocation: editData.firstBinLocation || "", parts: [] }]);
        }
      } else {
        // Create Mode
        setSelectedBranchId(currentBranchId || (branches[0] && branches[0].id) || "");
        setInwardDate(moment());
        setBinsTable([{ binLocation: "", parts: [] }]);
      }
      setActiveBinKey("0");
      setTableSearchText("");
      setPartOptions([]);
      setTimeout(() => {
        if (binInputRef.current) {
          binInputRef.current.focus();
        }
      }, 300);
    }
  }, [visible, editData, currentBranchId, branches]);

  const activeIndex = Math.min(
    Math.max(0, parseInt(activeBinKey, 10) || 0),
    binsTable.length - 1
  );
  const activeBin = binsTable[activeIndex] || { binLocation: "", parts: [] };

  const currentBinLocation = (activeBin.binLocation || "").trim();
  const isPermanent =
    Boolean(currentBinLocation) &&
    masterBinLocations.some((m) => {
      const mBranch = typeof m === "object" ? m.branch : null;
      const mLoc = (typeof m === "object" ? m.binLocation : m) || "";
      const matchesBranch = !mBranch || !selectedBranchId || mBranch === selectedBranchId;
      return (
        matchesBranch &&
        mLoc.trim().toUpperCase() === currentBinLocation.toUpperCase()
      );
    });
  const activeBinType = !currentBinLocation ? "-" : isPermanent ? "PERMANENT" : "TEMP";

  // Update bin location text
  const handleBinLocationChange = (idx, value) => {
    const updated = [...binsTable];
    updated[idx] = { ...updated[idx], binLocation: value };
    setBinsTable(updated);
  };

  // Add a new Bin tab
  const handleAddBinTab = () => {
    const newIdx = binsTable.length;
    setBinsTable([...binsTable, { binLocation: "", parts: [] }]);
    setActiveBinKey(String(newIdx));
    setTableSearchText("");
    setPartSearchText("");
    setPartOptions([]);
  };

  // Remove a Bin tab
  const handleRemoveBinTab = (targetKey) => {
    const removeIdx = parseInt(targetKey, 10);
    if (binsTable.length <= 1) return;
    const updated = binsTable.filter((_, i) => i !== removeIdx);
    setBinsTable(updated);
    const nextActive = Math.max(0, removeIdx - 1);
    setActiveBinKey(String(nextActive));
    setTableSearchText("");
    setPartSearchText("");
    setPartOptions([]);
  };

  // Search parts from backend partsMaster
  const handlePartSearch = (value) => {
    const trimmed = (value || "").trim();
    setPartSearchText(value || "");

    if (!trimmed) {
      setPartOptions([]);
      setSearchingParts(false);
      return;
    }

    if (partSearchDebounce.current) clearTimeout(partSearchDebounce.current);
    setSearchingParts(true);

    partSearchDebounce.current = setTimeout(async () => {
      try {
        const payload = {
          module: "partsMasters",
          column: "partNumber",
          searchString: trimmed,
          branch: selectedBranchId || undefined,
          fields: ["partNumber", "partName"],
          size: 50,
          searchColumns: ["partNumber", "partName"],
        };
        const response = await platformApi.post("/api/options/get", payload);
        if (response?.data?.code === 200) {
          const results = response.data.response || response.data.data || [];
          setPartOptions(results);
          if (results.length === 0) {
            message.warning(`Part "${trimmed}" is not available in database`);
          }
        } else {
          setPartOptions([]);
          message.warning(`Part "${trimmed}" is not available in database`);
        }
      } catch (err) {
        console.error("Error searching parts:", err);
        setPartOptions([]);
        message.warning(`Part "${trimmed}" is not available in database`);
      } finally {
        setSearchingParts(false);
      }
    }, 250);
  };

  // Select part from search dropdown to add to active bin
  const handleSelectPart = (partIdOrNum) => {
    const found = partOptions.find(
      (p) => (p.id && p.id === partIdOrNum) || p.partNumber === partIdOrNum
    );
    if (!found) return;

    addOrIncrementPart(found);
  };

  // Helper: Add or increment part in active bin
  const addOrIncrementPart = (partObj) => {
    const currentParts = activeBin.parts || [];
    const partNum = (partObj.partNumber || "").trim().toUpperCase();
    const existingIdx = currentParts.findIndex(
      (p) => (p.partNumber || "").trim().toUpperCase() === partNum
    );

    const updatedParts = [...currentParts];
    if (existingIdx >= 0) {
      updatedParts[existingIdx] = {
        ...updatedParts[existingIdx],
        quantity: (Number(updatedParts[existingIdx].quantity) || 0) + 1,
      };
      message.info(`Incremented quantity for ${partNum}`);
    } else {
      updatedParts.push({
        partId: partObj.id || undefined,
        partNumber: partObj.partNumber,
        partName: partObj.partName || "Part",
        quantity: 1,
      });
      message.success(`Added ${partNum}`);
    }

    const updatedBins = [...binsTable];
    updatedBins[activeIndex] = {
      ...activeBin,
      parts: updatedParts,
    };
    setBinsTable(updatedBins);
    setPartOptions([]);
    setPartSearchText("");
  };

  // Enter key press in Select / Scan Part input
  const handleSelectInputKeyDown = async (e) => {
    if (e.key === "Enter") {
      const query = (partSearchText || "").trim();
      if (!query) return;

      // If already in dropdown options, pick exact or first match
      if (partOptions.length > 0) {
        const exact =
          partOptions.find(
            (p) => (p.partNumber || "").trim().toUpperCase() === query.toUpperCase()
          ) || partOptions[0];
        e.preventDefault();
        addOrIncrementPart(exact);
        return;
      }

      // If options not loaded yet (e.g. rapid typing / direct paste / barcode scan), query backend directly
      e.preventDefault();
      setSearchingParts(true);
      try {
        const payload = {
          module: "partsMasters",
          column: "partNumber",
          searchString: query,
          branch: selectedBranchId || undefined,
          fields: ["partNumber", "partName"],
          size: 10,
          searchColumns: ["partNumber", "partName"],
        };
        const response = await platformApi.post("/api/options/get", payload);
        const results = response?.data?.response || response?.data?.data || [];
        const match =
          results.find(
            (p) => (p.partNumber || "").trim().toUpperCase() === query.toUpperCase()
          ) || results[0];

        if (match) {
          addOrIncrementPart(match);
        } else {
          message.warning(`Part "${query}" is not available in database`);
        }
      } catch (err) {
        console.error("Error looking up part:", err);
        message.warning(`Part "${query}" is not available in database`);
      } finally {
        setSearchingParts(false);
      }
    }
  };

  // Update quantity directly in table using partNumber
  const handleQuantityChange = (partNumber, val) => {
    const qty = Math.max(1, parseInt(val, 10) || 1);
    const currentParts = (activeBin.parts || []).map((p) => {
      if ((p.partNumber || "").trim().toUpperCase() === (partNumber || "").trim().toUpperCase()) {
        return { ...p, quantity: qty };
      }
      return p;
    });

    const updatedBins = [...binsTable];
    updatedBins[activeIndex] = { ...activeBin, parts: currentParts };
    setBinsTable(updatedBins);
  };

  // Remove part from active bin table using partNumber
  const handleRemovePart = (partNumber) => {
    const currentParts = (activeBin.parts || []).filter(
      (p) => (p.partNumber || "").trim().toUpperCase() !== (partNumber || "").trim().toUpperCase()
    );
    const updatedBins = [...binsTable];
    updatedBins[activeIndex] = { ...activeBin, parts: currentParts };
    setBinsTable(updatedBins);
  };

  // Hardware Scanner Gun listener (captures physical barcode scanner bursts)
  const processScannedBarcode = useCallback(
    async (code) => {
      if (!code) return;
      const cleanCode = code.trim();
      if (!cleanCode) return;

      // If active bin location is empty, set it
      if (!activeBin.binLocation || !activeBin.binLocation.trim()) {
        handleBinLocationChange(activeIndex, cleanCode);
        message.success(`Scanned Bin Location: ${cleanCode}`);
        return;
      }

      // Otherwise look up part and add to active bin
      try {
        const payload = {
          module: "partsMasters",
          column: "partNumber",
          searchString: cleanCode,
          branch: selectedBranchId || undefined,
          fields: ["partNumber", "partName"],
          size: 10,
          searchColumns: ["partNumber", "partName"],
        };
        const res = await platformApi.post("/api/options/get", payload);
        const options = res?.data?.response || res?.data?.data || [];
        const matched = options.find(
          (o) => (o.partNumber || "").trim().toUpperCase() === cleanCode.toUpperCase()
        );

        if (matched) {
          addOrIncrementPart(matched);
        } else if (options.length > 0) {
          addOrIncrementPart(options[0]);
        } else {
          message.warning(`Part "${cleanCode}" is not available in database`);
        }
      } catch (err) {
        console.error("Error looking up scanned part:", err);
        message.warning(`Part "${cleanCode}" is not available in database`);
      }
    },
    [activeBin, activeIndex, binsTable, selectedBranchId]
  );

  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e) => {
      const now = Date.now();
      const diff = now - lastKeyTimeRef.current;
      lastKeyTimeRef.current = now;

      if (e.key === "Enter") {
        const text = scannerBufferRef.current.trim();
        scannerBufferRef.current = "";
        if (text.length >= 3) {
          e.preventDefault();
          processScannedBarcode(text);
        }
        return;
      }

      if (diff > 70) {
        scannerBufferRef.current = "";
      }

      if (e.key && e.key.length === 1) {
        scannerBufferRef.current += e.key;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [visible, processScannedBarcode]);

  // Submit / Save Session
  const handleSubmit = async () => {
    if (!selectedBranchId) {
      message.error("Please select a branch");
      return;
    }

    const invalidBin = binsTable.find((b) => !b.binLocation || !b.binLocation.trim());
    if (invalidBin) {
      message.error("Please provide a Bin Location for all tabs");
      return;
    }

    let totalParts = 0;
    binsTable.forEach((b) => {
      totalParts += (b.parts || []).length;
    });

    if (totalParts === 0) {
      message.error("Please add at least one part");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        id: editData?.id || undefined,
        branchId: selectedBranchId,
        inwardDate: inwardDate ? inwardDate.format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
        bins: binsTable,
      };

      const res = await platformApi.post("/api/stockCheck/inward", payload);
      if (res?.data?.code === 200 || res?.status === 200) {
        message.success(
          editData?.id
            ? "Stock Checking updated & Spares Adjustments synchronized successfully"
            : "Stock Checking verified & Spares Adjustments created successfully"
        );
        try {
          sessionStorage.setItem("sparesAdjustmentHighlightTime", Date.now().toString());
          window.dispatchEvent(new CustomEvent("stockAdjustmentCreated"));
        } catch (e) {
          // ignore storage errors
        }
        onSuccess && onSuccess();
      } else {
        message.error(res?.data?.message || "Failed to save Stock Checking");
      }
    } catch (err) {
      console.error("Error saving stock checking:", err);
      message.error(err?.response?.data?.message || "Error saving Stock Checking");
    } finally {
      setSaving(false);
    }
  };

  // Parts Table Columns
  const partsColumns = [
    {
      title: "S.No",
      key: "sno",
      width: 60,
      render: (_, __, idx) => idx + 1,
    },
    {
      title: "Part Number",
      dataIndex: "partNumber",
      key: "partNumber",
      width: 180,
      render: (text) => <span style={{ fontWeight: "bold" }}>{text}</span>,
    },
    {
      title: "Part Name",
      dataIndex: "partName",
      key: "partName",
      width: 240,
      render: (text) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 120,
      render: (val, record) => (
        <InputNumber
          min={1}
          value={val}
          onChange={(newVal) => handleQuantityChange(record.partNumber, newVal)}
          style={{ width: "90px" }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title="Remove this part?"
          onConfirm={() => handleRemovePart(record.partNumber)}
          okText="Yes"
          cancelText="No"
        >
          <span className="linkylink" style={{ color: "#f5222d" }}>
            Delete
          </span>
        </Popconfirm>
      ),
    },
  ];

  // Totals
  let totalPartsCount = 0;
  let totalQtyCount = 0;
  binsTable.forEach((b) => {
    (b.parts || []).forEach((p) => {
      totalPartsCount += 1;
      totalQtyCount += Number(p.quantity) || 0;
    });
  });

  return (
    <Modal
      title={editData ? "Modify Stock Checking" : "Add Stock Checking"}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Spin spinning={saving}>
        <Form layout="vertical">
          {/* Top Form Fields: Branch & Inward Date */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Branch" required>
                <Select
                  placeholder="Select Branch"
                  value={selectedBranchId}
                  onChange={(val) => setSelectedBranchId(val)}
                >
                  {branches.map((b) => (
                    <Option key={b.id} value={b.id}>
                      {b.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Inward Date" required>
                <DatePicker
                  format="DD/MM/YYYY"
                  value={inwardDate}
                  onChange={(date) => setInwardDate(date)}
                  style={{ width: "100%" }}
                  placeholder="Select Date"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Bin Locations Tabs */}
          <div style={{ marginTop: 4, marginBottom: 8 }}>
            <span style={{ fontWeight: 600, color: "#262626", fontSize: "13px" }}>
              Bin Locations & Parts
            </span>
          </div>

          {/* Static Add Bin Button Styles (no hover / focus / active color changes) */}
          <style>{`
            .stockcheck-static-add-btn,
            .stockcheck-static-add-btn:hover,
            .stockcheck-static-add-btn:focus,
            .stockcheck-static-add-btn:active {
              background-color: #006d75 !important;
              border-color: #006d75 !important;
              color: #ffffff !important;
              box-shadow: none !important;
            }
            .stockcheck-static-add-btn *,
            .stockcheck-static-add-btn:hover *,
            .stockcheck-static-add-btn:focus *,
            .stockcheck-static-add-btn:active * {
              color: #ffffff !important;
            }
          `}</style>

          <Tabs
            type="editable-card"
            activeKey={activeBinKey}
            onChange={(key) => {
              setActiveBinKey(key);
              setTableSearchText("");
            }}
            hideAdd
            tabBarExtraContent={
              <Button
                type="primary"
                icon="plus"
                onClick={handleAddBinTab}
                className="stockcheck-static-add-btn"
                style={{
                  backgroundColor: "#006d75",
                  borderColor: "#006d75",
                  color: "#ffffff",
                  fontWeight: 600,
                  fontSize: "13px",
                  height: "32px",
                  padding: "0 14px",
                  marginBottom: 4,
                }}
              >
                Add Bin
              </Button>
            }
            onEdit={(targetKey, action) => {
              if (action === "add") handleAddBinTab();
              else if (action === "remove") handleRemoveBinTab(targetKey);
            }}
          >
            {binsTable.map((bin, bIdx) => {
              const tabTitle = bin.binLocation ? bin.binLocation : `Bin ${bIdx + 1}`;
              const filteredParts = (bin.parts || []).filter((p) => {
                if (!tableSearchText || !tableSearchText.trim()) return true;
                const q = tableSearchText.trim().toLowerCase();
                const partNo = (p.partNumber || "").toLowerCase();
                const partDesc = (p.partName || "").toLowerCase();
                return partNo.includes(q) || partDesc.includes(q);
              });

              return (
                <TabPane
                  tab={tabTitle}
                  key={String(bIdx)}
                  closable={binsTable.length > 1}
                >
                  <Row gutter={16} style={{ marginTop: 8 }}>
                    <Col span={10}>
                      <Form.Item label="Bin Location" required style={{ marginBottom: 12 }}>
                        <Input
                          placeholder="Enter or scan Bin Location"
                          value={bin.binLocation}
                          onChange={(e) => handleBinLocationChange(bIdx, e.target.value)}
                          ref={bIdx === activeIndex ? binInputRef : null}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={14}>
                      <Form.Item label="Select / Scan Part" style={{ marginBottom: 12 }}>
                        <Select
                          showSearch
                          placeholder="Search by Part Number / Name or Scan"
                          value={undefined}
                          onSearch={handlePartSearch}
                          onSelect={handleSelectPart}
                          onInputKeyDown={handleSelectInputKeyDown}
                          filterOption={false}
                          defaultActiveFirstOption={true}
                          notFoundContent={
                            searchingParts ? (
                              <div style={{ padding: "8px", textAlign: "center", color: "#8c8c8c" }}>
                                <Spin size="small" style={{ marginRight: 8 }} />
                                Searching parts database...
                              </div>
                            ) : partSearchText && partSearchText.trim() ? (
                              <div style={{ padding: "8px", textAlign: "center", color: "#d46b08", fontWeight: 500 }}>
                                Part is not available in database
                              </div>
                            ) : (
                              <div style={{ padding: "8px", textAlign: "center", color: "#8c8c8c" }}>
                                Type to search or scan parts
                              </div>
                            )
                          }
                          style={{ width: "100%" }}
                        >
                          {partOptions.map((p) => (
                            <Option key={p.id || p.partNumber} value={p.id || p.partNumber}>
                              <strong>{p.partNumber}</strong> - {p.partName || "N/A"}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Search field below Bin Location field */}
                  <Row gutter={16}>
                    <Col span={10}>
                      <Form.Item label="Search Added Parts" style={{ marginBottom: 12 }}>
                        <Input.Search
                          placeholder="Search by Part No or Description..."
                          value={tableSearchText}
                          onChange={(e) => setTableSearchText(e.target.value)}
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Parts Table */}
                  <Table
                    size="small"
                    dataSource={filteredParts}
                    columns={partsColumns}
                    rowKey={(record, idx) => record.partNumber || idx}
                    pagination={false}
                    scroll={filteredParts.length > 7 ? { y: 280 } : undefined}
                    locale={{
                      emptyText:
                        (bin.parts || []).length === 0
                          ? "No parts added to this bin yet. Search or scan part above."
                          : "No parts match your search filter.",
                    }}
                    style={{ marginTop: 4, marginBottom: 8 }}
                  />
                </TabPane>
              );
            })}
          </Tabs>

          {/* Footer with Totals and Action Buttons */}
          <div
            style={{
              borderTop: "1px solid #e8e8e8",
              marginTop: 16,
              paddingTop: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ fontSize: "16px", fontWeight: 500, color: "#262626" }}>
              <span>Bin Type: </span>
              <strong
                style={{
                  color:
                    activeBinType === "PERMANENT"
                      ? "#006d75"
                      : activeBinType === "TEMP"
                      ? "#d46b08"
                      : "#8c8c8c",
                  marginRight: 16,
                }}
              >
                {activeBinType}
              </strong>
              <span style={{ color: "#d9d9d9", marginRight: 16 }}>|</span>
              <span>Total Parts: </span>
              <strong style={{ marginRight: 16 }}>{totalPartsCount}</strong>
              <span style={{ color: "#d9d9d9", marginRight: 16 }}>|</span>
              <span>Total Quantity: </span>
              <strong style={{ color: "#006d75" }}>{totalQtyCount}</strong>
            </div>
            <div>
              <Button onClick={onClose} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button type="primary" onClick={handleSubmit} loading={saving}>
                Submit
              </Button>
            </div>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default StockInwardModal;
